// src/components/Control/Operator/MobileLayout.tsx
// Телефон в портретной ориентации: отдельная раскладка, не уменьшенный desktop.
//   Карта — почти весь экран, превью камеры поверх, статус внизу карты;
//   Камера — во весь экран с маленькой картой;
//   Няня — отдельный экран;
//   Управление — нижняя шторка (кнопка «Управление» или свайп вверх от нижнего края);
//   Настройки — отдельный экран (шестерёнка в шапке). STOP и голос — в шапке всегда.
// Pi без шасси: экраны «Камера» и «Няня», без карты, управления и STOP.
import React, { useRef } from 'react';
import { useHasChassis, useRobot, useUi, useUiActions, Screen } from '../robot/RobotContext';
import Header from './Header';
import StatusStrip from './StatusStrip';
import { CameraPreview, CameraStage, CamSelector, CameraActions, CAM_LABELS, useCamChoice } from './Camera';
import NannyScreen from './NannyScreen';
import Settings from './Settings';
import ControlPad from './ControlPad';
import MapViewer from '../ControlPanel/MapViewer';
import Cam2Obstacle from '../ControlPanel/Cam2Obstacle';
import { Icon } from './ui';
import { useCameraFeed } from '../robot/cameraFeed';
import { useTick } from '../../../hooks/useTick';

const ControlFab: React.FC = () => {
  const { toggleControl } = useUiActions();
  return (
    <button type="button" className="op-fab" onClick={() => toggleControl(true)} aria-label="Открыть управление">
      <Icon name="gamepad" /> Управление
    </button>
  );
};

// Свайп вверх от нижнего края — открыть управление
const EdgeSwipe: React.FC = () => {
  const { toggleControl } = useUiActions();
  const start = useRef<number | null>(null);
  return (
    <div className="op-edge-swipe" aria-hidden="true"
         onPointerDown={e => { start.current = e.clientY; }}
         onPointerUp={e => { if (start.current !== null && start.current - e.clientY > 30) toggleControl(true); start.current = null; }}
         onPointerCancel={() => { start.current = null; }} />
  );
};

const ControlSheet: React.FC = () => {
  const open = useUi(u => u.controlOpen);
  const { toggleControl } = useUiActions();
  const start = useRef<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  if (!open) return null;
  return (
    <div className="op-sheet-backdrop" onPointerDown={e => e.target === e.currentTarget && toggleControl(false)}>
      <div ref={sheetRef} className="op-sheet" role="dialog" aria-label="Управление роботом">
        <div className="op-sheet-head"
             onPointerDown={e => { start.current = e.clientY; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }}
             onPointerMove={e => {
               if (start.current === null || !sheetRef.current) return;
               sheetRef.current.style.transform = `translateY(${Math.max(0, e.clientY - start.current)}px)`;
             }}
             onPointerUp={e => {
               const dy = start.current === null ? 0 : e.clientY - start.current;
               start.current = null;
               if (sheetRef.current) sheetRef.current.style.transform = '';
               if (dy > 60) toggleControl(false);
             }}>
          <span className="op-sheet-grip" aria-hidden="true" />
          <span className="op-sheet-title">Управление</span>
          <button type="button" className="op-icon-btn" aria-label="Свернуть управление" onClick={() => toggleControl(false)}>
            <Icon name="chevronDown" />
          </button>
        </div>
        <ControlPad variant="mobile" />
      </div>
    </div>
  );
};

const CameraStatus: React.FC = () => {
  useTick(1000);
  const { session, robotId } = useRobot();
  const cam = useCamChoice();
  const show = useUi(u => u.show);
  const one = cam === '1' ? '1' : '0';
  // Та же общая подписка, что и у окна видео — второй подписки не создаёт
  const feed = useCameraFeed(session, show[one] ? `robot/${robotId}/cams/${one}` : null);
  const ok = !!feed.at && Date.now() - feed.at < 3000;
  return (
    <span className={`op-cam-state ${ok ? 'ok' : 'bad'}`}>
      <span aria-hidden="true">{ok ? '●' : '■'}</span> {ok ? 'Online' : 'Нет кадров'}{ok ? ` · ${feed.fps.toFixed(0)} FPS` : ''}
    </span>
  );
};

const MobileCamera: React.FC = () => {
  const { session, robotId } = useRobot();
  const cam = useCamChoice();
  const chassis = useHasChassis();
  const { go } = useUiActions();
  return (
    <div className="op-m-camera">
      <div className="op-m-camera-top">
        {chassis && <button type="button" className="op-icon-btn" aria-label="К карте" onClick={() => go('map')}><Icon name="back" /></button>}
        <span className="op-m-camera-title">{cam === 'split' ? 'Обе камеры' : CAM_LABELS[cam]}</span>
        <CameraActions menu />
      </div>
      <div className="op-m-camera-stage">
        <CameraStage choice={cam} />
        {chassis && (
          <div className="op-m-mappreview">
            <MapViewer zenohSession={session} robotId={robotId} compact onActivate={() => go('map')} />
          </div>
        )}
      </div>
      <div className="op-m-camera-bottom">
        <CamSelector />
        <CameraStatus />
        {(cam === '1' || cam === 'split') && <Cam2Obstacle compact />}
      </div>
    </div>
  );
};

const NAV: [Screen, string, string][] = [['map', 'map', 'Карта'], ['camera', 'camera', 'Камера'], ['nanny', 'nanny', 'Няня']];

const MobileLayout: React.FC = () => {
  const { session, robotId } = useRobot();
  const chassis = useHasChassis();
  const ui = useUi(u => u.screen);
  // без шасси карты нет: её экран — камера
  const screen: Screen = !chassis && ui === 'map' ? 'camera' : ui;
  const { go } = useUiActions();

  return (
    <div className="op-app mobile">
      <Header variant="mobile" />
      <div className="op-m-body">
        {/* Карта остаётся смонтированной — чтобы не терять след */}
        {chassis && (
          <div className="op-m-map" hidden={screen !== 'map'}>
            <MapViewer zenohSession={session} robotId={robotId} />
            {screen === 'map' && <CameraPreview />}
            <div className="op-m-map-bottom">
              <StatusStrip compact />
              <ControlFab />
            </div>
          </div>
        )}
        {screen === 'camera' && (
          <>
            <MobileCamera />
            {chassis && <div className="op-m-fab-wrap"><ControlFab /></div>}
          </>
        )}
        {screen === 'nanny' && <NannyScreen variant="mobile" />}
        {screen === 'settings' && <Settings variant="mobile" />}
        {chassis && (screen === 'map' || screen === 'camera') && <EdgeSwipe />}
      </div>
      {screen !== 'settings' && (
        <nav className="op-bottom-nav" aria-label="Экраны">
          {NAV.filter(([id]) => chassis || id !== 'map').map(([id, icon, label]) => (
            <button key={id} type="button" className={screen === id ? 'on' : ''} aria-current={screen === id ? 'page' : undefined} onClick={() => go(id)}>
              <Icon name={icon} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      )}
      {chassis && <ControlSheet />}
    </div>
  );
};

export default MobileLayout;
